import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { checkInterventionFormEnable } from "./data";
import { getInterventionById } from "../../../../redux/modules/intervention/actions";
import { ClinicalServiceTypes } from "../../text/data";

// Import existing form components
import MedicationReviewForms from "../../../patient/clinical-services/services/medication-review/documentationForm";
import MinorAilemntForms from "../../../patient/clinical-services/services/minor-ailment/documentationForm";

// Import intervention form component
import InterventionForm from "./InterventionForm";

const LoadInterventionForms = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientId, interventionId, type, formType } = useParams();
  const { currentIntervention, clinicalServiceObj, interventions } = useSelector((state) => state.intervention);
  const {
    medications,
  } = useSelector((state) => state.clinicalService);
  const { patient } = useSelector((state) => state.patients);

  const interventionFormRoot = currentIntervention?.interventionAssessment?.interventionAssessmentForm || {};
  const interventionDetails = interventionFormRoot?.interventionDetails || {};
  const interventionMedicationId = interventionDetails?.medicationId || currentIntervention?.medicationId || null;
  const matchingMedication = useMemo(() => {
    if (!Array.isArray(medications)) {
      return null;
    }

    return medications.find((med) => med.id === interventionMedicationId) || null;
  }, [medications, interventionMedicationId]);

  const interventionMedicationRecord = useMemo(() => {
    if (!interventionDetails?.medicationName && !matchingMedication?.medicationName && !matchingMedication?.displayName) {
      return null;
    }

    return {
      id: interventionMedicationId || matchingMedication?.id || interventionDetails?.medicationName || "intervention-medication",
      medicationName:
        interventionDetails?.medicationName || matchingMedication?.medicationName || matchingMedication?.displayName || "",
      dosage: interventionDetails?.dosage || matchingMedication?.dosage || "",
      form: interventionDetails?.form || matchingMedication?.form || "",
      route: interventionDetails?.route || matchingMedication?.route || "",
      frequency: interventionDetails?.frequency || matchingMedication?.frequency || "",
      reasonForMedicationName:
        interventionDetails?.reason || interventionDetails?.drugTherapyProblem || matchingMedication?.reasonForMedicationName || "",
      reasonsForMedicationGenCode:
        interventionDetails?.reasonGenCode || matchingMedication?.reasonsForMedicationGenCode || "",
      comments: interventionDetails?.comments || matchingMedication?.comments || "",
      specialIntructions:
        interventionDetails?.specialInstructions || matchingMedication?.specialIntructions || "",
      adherence: matchingMedication?.adherence,
      safety: matchingMedication?.safety,
      properTechnique: matchingMedication?.properTechnique,
      effectiveness: matchingMedication?.effectiveness,
      intervention: interventionDetails?.drugTherapyProblem || matchingMedication?.intervention || null,
      status: matchingMedication?.status || "Completed",
      generalTime: matchingMedication?.generalTime || [],
      dispenseQuantity: matchingMedication?.dispenseQuantity ?? null,
    };
  }, [interventionDetails, interventionMedicationId, matchingMedication]);

  /** Rows saved on the intervention / assessment (sparse; used inside `clinicalServiceData` for forms). */
  const pmrPatientMedications = useMemo(() => {
    if (currentIntervention && (type === "AMR" || type === "MA")) {
      if (interventionFormRoot?.medications?.medications?.length) {
        return interventionFormRoot.medications.medications;
      }

      return interventionMedicationRecord ? [interventionMedicationRecord] : [];
    }

    return medications;
  }, [currentIntervention, interventionFormRoot?.medications?.medications, interventionMedicationRecord, medications, type]);

  /**
   * Patient profile medication list from Medication Review (Redux). Passed to documentation forms as
   * `medications` → FormContext `patientMedications` so PMR and other docs can merge intervention rows
   * with full drug name, route, frequency, etc. Intervention-only snapshots omit those fields.
   */
  const documentationPatientMedications = useMemo(() => {
    const fromClinicalService = medications;
    if (Array.isArray(fromClinicalService) && fromClinicalService.length > 0) {
      return fromClinicalService;
    }
    return Array.isArray(pmrPatientMedications) ? pmrPatientMedications : [];
  }, [medications, pmrPatientMedications]);

  const resolvedServiceName = useMemo(() => {
    return (
      currentIntervention?.clinicalServiceTypeCode ||
      (type === "AMR"
        ? ClinicalServiceTypes.MedicationReview
        : type === "MA"
          ? ClinicalServiceTypes.AllergicRhinitis
          : "")
    );
  }, [currentIntervention?.clinicalServiceTypeCode, type]);

  const clinicalServiceData = useMemo(() => {
    if (clinicalServiceObj) {
      return clinicalServiceObj;
    }

    if (!currentIntervention || (type !== "AMR" && type !== "MA")) {
      return null;
    }

    const fallbackInitialAssessmentForm = {
      ...(currentIntervention.interventionAssessment?.interventionAssessmentForm || {}),
      medications:
        currentIntervention.interventionAssessment?.interventionAssessmentForm?.medications || {
          medications: pmrPatientMedications || [],
        },
    };

    return {
      id: null,
      serviceName: resolvedServiceName,
      initialAssessment: {
        initialAssessmentForm: fallbackInitialAssessmentForm,
      },
      medications: pmrPatientMedications || [],
    };
  }, [clinicalServiceObj, currentIntervention, pmrPatientMedications, resolvedServiceName, type]);

  // Load intervention from URL on mount or when interventionId changes
  useEffect(() => {
    if (interventionId && patientId) {
      const shouldFetch = !currentIntervention || currentIntervention.id !== interventionId;
      if (shouldFetch) {
        dispatch(getInterventionById(patientId, interventionId));
      }
    }
  }, [interventionId, patientId, currentIntervention, dispatch]);

  const isFormEnabled = (type === "AMR" || type === "MA") ? true : checkInterventionFormEnable(formType, currentIntervention);

  const renderForm = () => {
    if (!isFormEnabled) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-white">
          <div className="text-center">
            <div className="text-gray-400 text-xl font-medium mb-4">
              Form Not Available
            </div>
            <div className="text-gray-500 text-base">
              This form is not available for the current intervention state.
            </div>
          </div>
        </div>
      );
    }

    switch (type) {
      case "AMR":
        return (
          <MedicationReviewForms
            clinicalServiceObj={clinicalServiceData}
            clinicalServiceName={resolvedServiceName}
            medications={documentationPatientMedications}
            patient={patient}
            formType={formType}
            onBack={(e) =>
              location?.state?.fromDocumentationForms
                ? navigate(`/patient/${patientId}/interventions/${interventionId}`, {
                    state: { forceDocumentationForms: true },
                  })
                : navigate(`/patient/${patientId}/interventions/${interventionId}`)
            }
          />
        );
      case "MA":
        return (
          <MinorAilemntForms
            clinicalServiceObj={clinicalServiceData}
            clinicalServiceName={resolvedServiceName}
            patient={patient}
            formType={formType}
            onBack={(e) =>
              location?.state?.fromDocumentationForms
                ? navigate(`/patient/${patientId}/interventions/${interventionId}`, {
                    state: { forceDocumentationForms: true },
                  })
                : navigate(`/patient/${patientId}/interventions/${interventionId}`)
            }
          />
        );
       case "INV":
         return (
           <InterventionForm
             intervention={currentIntervention}
             formType={formType}
             onBack={() =>
                location?.state?.fromDocumentationForms
                  ? navigate(`/patient/${patientId}/interventions/${interventionId}`, {
                      state: { forceDocumentationForms: true },
                    })
                  : navigate(`/patient/${patientId}/interventions/${interventionId}`)
             }
           />
         );
        
      default:
        return (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 text-xl font-medium mb-4">
                Invalid Form Type
              </div>
              <div className="text-gray-500 text-base">
                The form type "{type}" is not recognized.
              </div>
            </div>
          </div>
        );
    }
  };

  if (!currentIntervention && type === "INV") {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-xl font-medium mb-4">
            Loading Intervention...
          </div>
          <div className="text-gray-500 text-base">
            Please wait while we load the intervention data.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {renderForm()}
    </div>
  );
};

export default LoadInterventionForms;